import React, { useState } from 'react';
import { PhoneInputProps, PhoneValidation, UserBaseline, HalalFoodCategory, AlcoholPolicyCategory } from '../types';

const PhoneInputForm: React.FC<PhoneInputProps> = ({ onCallInitiate, isProcessing }) => {
  const [countryCode, setCountryCode] = useState('+44');  // Default UK
  const [localNumber, setLocalNumber] = useState('');
  const [validation, setValidation] = useState<PhoneValidation>({ isValid: false, formattedNumber: '' });
  const [showBaseline, setShowBaseline] = useState(false);
  const [baseline, setBaseline] = useState<UserBaseline>({});
  const [propertyName, setPropertyName] = useState('');
  const [propertyUrl, setPropertyUrl] = useState('');

  const validatePhoneNumber = (localNum: string): PhoneValidation => {
    const combined = countryCode + localNum.replace(/\D/g, '');
    const cleaned = combined.replace(/\D/g, '');
    const isValid = cleaned.length >= 10 && cleaned.length <= 15;

    return {
      isValid,
      formattedNumber: combined,
      errorMessage: isValid ? undefined : 'Please enter a valid local phone number (7-10 digits for UK)',
    };
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value.startsWith('+')) {
      setCountryCode(value);
    } else {
      setCountryCode('+' + value);
    }
    // Re-validate
    setValidation(validatePhoneNumber(localNumber));
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalNumber(value);
    setValidation(validatePhoneNumber(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validation.isValid || isProcessing) return;

    try {
      await onCallInitiate(validation.formattedNumber, baseline, propertyName || undefined, propertyUrl || undefined);
      setLocalNumber(''); // Clear local on success
      setValidation({ isValid: false, formattedNumber: '' });
      setBaseline({});
      setShowBaseline(false);
      setPropertyName('');
      setPropertyUrl('');
    } catch (error) {
      console.error('Failed to initiate call:', error);
      // Error handling will be shown in parent component
    }
  };

  return (
    <div className="phone-input-section">
      <h2>Initiate Property Verification Call</h2>
      <form onSubmit={handleSubmit} className="phone-form">
        <div className="input-row">
          <div className="phone-fields">
            <input
              type="tel"
              value={countryCode}
              onChange={handleCountryChange}
              placeholder="Country Code (e.g., +1)"
              disabled={isProcessing}
              className="phone-code-input"
              maxLength={5}
            />
            <input
              type="tel"
              value={localNumber}
              onChange={handleLocalChange}
              placeholder="Local Number (e.g., 7912345678)"
              disabled={isProcessing}
              className={`phone-input ${!validation.isValid && localNumber ? 'invalid' : ''}`}
            />
            <input
              type="text"
              value={propertyName}
              onChange={(e) => setPropertyName(e.target.value)}
              placeholder="Property Name (optional)"
              disabled={isProcessing}
              className="property-input"
            />
            <input
              type="url"
              value={propertyUrl}
              onChange={(e) => setPropertyUrl(e.target.value)}
              placeholder="Property URL (optional)"
              disabled={isProcessing}
              className="property-input"
            />
          </div>
          {validation.errorMessage && (
            <div className="error-message">{validation.errorMessage}</div>
          )}
          <small className="format-hint">Full number will be {countryCode}{localNumber.replace(/\D/g, '')}</small>
        </div>

        <div className="button-row">
          <button
            type="submit"
            disabled={!validation.isValid || isProcessing}
            className="call-button"
          >
            {isProcessing ? 'Initiating Call...' : 'Initiate Verification Call'}
          </button>

          <button
            type="button"
            className="baseline-toggle"
            onClick={() => setShowBaseline(!showBaseline)}
          >
            {showBaseline ? 'Hide Pre-Filled Data' : 'Add Pre-Filled Data'}
          </button>
        </div>

        {showBaseline && (
          <div className="baseline-panel">
            <h3>Pre-Filled Current Details</h3>
            <div className="baseline-row">
              <label>Halal Food</label>
              <select
                value={baseline.halalFood || ''}
                onChange={(e) => setBaseline({ ...baseline, halalFood: (e.target.value || undefined) as HalalFoodCategory })}
              >
                <option value="">—</option>
                <option value="all">All</option>
                <option value="some">Some</option>
                <option value="on_request">On request</option>
                <option value="nearby">Nearby only</option>
                <option value="none">None</option>
              </select>
            </div>

            <div className="baseline-row">
              <label>Alcohol Policy</label>
              <select
                value={baseline.alcoholPolicies || ''}
                onChange={(e) => setBaseline({ ...baseline, alcoholPolicies: (e.target.value || undefined) as AlcoholPolicyCategory })}
              >
                <option value="">—</option>
                <option value="alcohol_free">Alcohol-free property</option>
                <option value="restaurant_only">Alcohol-free restaurant</option>
                <option value="room_only">Alcohol-free room</option>
                <option value="none">None</option>
              </select>
            </div>

            <div className="baseline-row split">
              <label>Halal Options</label>
              <div className="ticks">
                <label><input type="checkbox" checked={baseline.halalOptions?.all === 'yes'} onChange={(e) => setBaseline({ ...baseline, halalOptions: { ...baseline.halalOptions, all: e.target.checked ? 'yes' : 'no' } })}/> All</label>
                <label><input type="checkbox" checked={baseline.halalOptions?.some === 'yes'} onChange={(e) => setBaseline({ ...baseline, halalOptions: { ...baseline.halalOptions, some: e.target.checked ? 'yes' : 'no' } })}/> Some</label>
                <label><input type="checkbox" checked={baseline.halalOptions?.onRequest === 'yes'} onChange={(e) => setBaseline({ ...baseline, halalOptions: { ...baseline.halalOptions, onRequest: e.target.checked ? 'yes' : 'no' } })}/> On request</label>
                <label><input type="checkbox" checked={baseline.halalOptions?.nearby === 'yes'} onChange={(e) => setBaseline({ ...baseline, halalOptions: { ...baseline.halalOptions, nearby: e.target.checked ? 'yes' : 'no' } })}/> Nearby</label>
              </div>
            </div>

            <div className="baseline-row split">
              <label>Alcohol Options</label>
              <div className="ticks">
                <label><input type="checkbox" checked={baseline.alcoholOptions?.property === 'yes'} onChange={(e) => setBaseline({ ...baseline, alcoholOptions: { ...baseline.alcoholOptions, property: e.target.checked ? 'yes' : 'no' } })}/> Property alcohol-free</label>
                <label><input type="checkbox" checked={baseline.alcoholOptions?.restaurant === 'yes'} onChange={(e) => setBaseline({ ...baseline, alcoholOptions: { ...baseline.alcoholOptions, restaurant: e.target.checked ? 'yes' : 'no' } })}/> Alcohol-free restaurant</label>
                <label><input type="checkbox" checked={baseline.alcoholOptions?.room === 'yes'} onChange={(e) => setBaseline({ ...baseline, alcoholOptions: { ...baseline.alcoholOptions, room: e.target.checked ? 'yes' : 'no' } })}/> Alcohol removed from rooms</label>
              </div>
            </div>

            <div className="baseline-row split">
              <label>Leisure</label>
              <div className="ticks">
                <label><input type="checkbox" checked={baseline.leisureFacilities?.modestSwimwear === 'yes'} onChange={(e) => setBaseline({ ...baseline, leisureFacilities: { ...baseline.leisureFacilities, modestSwimwear: e.target.checked ? 'yes' : 'no' } })}/> Modest Swimwear</label>
                <label><input type="checkbox" checked={baseline.leisureFacilities?.womenOnlyFacilities === 'yes'} onChange={(e) => setBaseline({ ...baseline, leisureFacilities: { ...baseline.leisureFacilities, womenOnlyFacilities: e.target.checked ? 'yes' : 'no' } })}/> Women-only Facilities</label>
                <label><input type="checkbox" checked={baseline.leisureFacilities?.privateHire === 'yes'} onChange={(e) => setBaseline({ ...baseline, leisureFacilities: { ...baseline.leisureFacilities, privateHire: e.target.checked ? 'yes' : 'no' } })}/> Private Hire</label>
              </div>
            </div>
          </div>
        )}
      </form>

      <style>{`
        .phone-fields {
          display: flex;
          gap: 0.5rem;
          align-items: stretch;
          width: 100%;
        }

        .input-row {
          margin-bottom: 1rem;
        }

        .button-row {
          display: flex;
          gap: 1rem;
          justify-content: center;
          align-items: center;
        }

        .phone-code-input {
          width: 120px;
          padding: 0.75rem 0.875rem;
          font-size: 0.9rem;
          border: 2px solid var(--hb-border);
          border-radius: var(--hb-radius) 0 0 var(--hb-radius);
          background: var(--hb-bg-secondary);
          transition: all 0.2s;
          font-weight: 500;
        }

        .phone-code-input:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 3px rgba(14, 165, 180, 0.1);
          background: white;
        }

        .phone-input {
          flex: 1;
          min-width: 160px;
          padding: 0.75rem 0.875rem;
          font-size: 0.9rem;
          border: 2px solid var(--hb-border);
          border-radius: 0;
          background: var(--hb-bg-secondary);
          transition: all 0.2s;
        }

        .phone-input:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 3px rgba(14, 165, 180, 0.1);
          background: white;
        }

        .phone-input.invalid {
          border-color: #dc3545;
          background: #fef2f2;
        }

        .property-input {
          min-width: 140px;
          flex: 1;
          padding: 0.75rem 0.875rem;
          font-size: 0.9rem;
          border: 2px solid var(--hb-border);
          border-radius: 0 var(--hb-radius) var(--hb-radius) 0;
          background: var(--hb-bg-secondary);
          transition: all 0.2s;
        }

        .property-input:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 3px rgba(14, 165, 180, 0.1);
          background: white;
        }

        .format-hint {
          font-size: 0.8rem;
          color: var(--hb-muted);
          display: block;
          margin-top: 0.25rem;
          font-weight: 500;
        }

        .phone-input-section {
          background: var(--hb-bg-secondary);
          padding: 1.5rem;
          border-radius: var(--hb-radius-lg);
          box-shadow: var(--hb-shadow-lg);
          border: 1px solid var(--hb-border);
          margin-bottom: 1.5rem;
        }

        .phone-input-section h2 {
          margin: 0 0 1rem 0;
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--hb-slate);
          text-align: center;
        }

        .phone-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .input-group {
          position: relative;
        }

        .error-message {
          color: #dc2626;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          padding: 0.375rem 0.625rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: var(--hb-radius);
          font-weight: 500;
        }

        .call-button {
          padding: 0.75rem 1.5rem;
          font-size: 0.95rem;
          font-weight: 600;
          background: linear-gradient(135deg, var(--hb-sea) 0%, var(--hb-sea-dark) 100%);
          color: white;
          border: none;
          border-radius: var(--hb-radius);
          cursor: pointer;
          transition: all 0.2s;
          box-shadow: var(--hb-shadow);
        }

        .call-button:hover:not(:disabled) {
          background: linear-gradient(135deg, var(--hb-sea-dark) 0%, #0a5f6a 100%);
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(14, 165, 180, 0.3);
        }

        .call-button:disabled {
          background: var(--hb-muted);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .baseline-toggle {
          background: var(--hb-orange);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-size: 0.95rem;
          font-weight: 600;
          transition: all 0.2s;
          box-shadow: var(--hb-shadow);
        }

        .baseline-toggle:hover {
          background: var(--hb-orange-dark);
          transform: translateY(-1px);
        }

        .baseline-panel {
          margin-top: 1.5rem;
          background: var(--hb-bg);
          border: 1px solid var(--hb-border);
          border-radius: var(--hb-radius);
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          box-shadow: var(--hb-shadow);
        }

        .baseline-panel h3 {
          margin: 0 0 0.5rem 0;
          font-size: 1.2rem;
          color: var(--hb-slate);
          font-weight: 600;
        }

        .baseline-row {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .baseline-row.split {
          align-items: flex-start;
        }

        .baseline-row label {
          min-width: 150px;
          font-size: 0.95rem;
          color: var(--hb-slate);
          font-weight: 500;
        }

        .baseline-row select {
          padding: 0.5rem 0.75rem;
          border-radius: var(--hb-radius);
          border: 2px solid var(--hb-border);
          background: var(--hb-bg-secondary);
          font-size: 0.9rem;
          transition: all 0.2s;
          flex: 1;
        }

        .baseline-row select:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 3px rgba(14, 165, 180, 0.1);
          background: white;
        }

        .ticks {
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
        }

        .ticks label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          color: var(--hb-slate);
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          transition: background-color 0.2s;
        }

        .ticks label:hover {
          background: var(--hb-bg);
        }

        .ticks input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: var(--hb-sea);
        }
      `}</style>
    </div>
  );
};

export default PhoneInputForm;



